import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { addParticipantsBulk, MAX_PARTICIPANTS } from '@/data/participants';
import type { Discipline, PlayerInfo } from '@/data/types';

interface Props {
  tournamentId: string;
  discipline: Discipline;
  currentCount: number;
}

const SURNAMES = [
  'Иванов', 'Петров', 'Сидоров', 'Кузнецов', 'Смирнов', 'Попов', 'Васильев',
  'Соколов', 'Михайлов', 'Новиков', 'Фёдоров', 'Морозов', 'Волков', 'Алексеев',
  'Лебедев', 'Семёнов', 'Егоров', 'Павлов', 'Козлов', 'Степанов',
];

/** Имя тестового игрока по сквозному индексу: «Иванов 1», «Петров 2», … */
function testPlayerName(index: number): string {
  const surname = SURNAMES[index % SURNAMES.length];
  return `${surname} ${index + 1}`;
}

export function GenerateTestParticipantsButton({
  tournamentId,
  discipline,
  currentCount,
}: Props) {
  const remaining = MAX_PARTICIPANTS - currentCount;
  const [count, setCount] = useState('8');
  const [busy, setBusy] = useState(false);

  async function handleGenerate() {
    const n = Number(count);
    if (!Number.isInteger(n) || n < 1) {
      toast.error('Укажите количество (целое число ≥ 1)');
      return;
    }
    if (n > remaining) {
      toast.error(`Свободных мест: ${remaining} (лимит ${MAX_PARTICIPANTS})`);
      return;
    }

    setBusy(true);
    try {
      // Сквозной индекс игроков, чтобы имена не повторялись с уже добавленными
      // и между собой (для пар — 2 разных игрока на участника).
      let playerIdx = currentCount * (discipline === 'DOUBLES' ? 2 : 1);
      const inputs: { players: PlayerInfo[] }[] = [];
      for (let i = 0; i < n; i++) {
        if (discipline === 'SINGLES') {
          inputs.push({ players: [{ name: testPlayerName(playerIdx++) }] });
        } else {
          inputs.push({
            players: [
              { name: testPlayerName(playerIdx++) },
              { name: testPlayerName(playerIdx++) },
            ],
          });
        }
      }
      await addParticipantsBulk(tournamentId, inputs);
      toast.success(`Добавлено тестовых: ${n}`);
    } catch (err) {
      toast.error('Не получилось', { description: String((err as Error).message) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row gap-2">
      <input
        className="input"
        type="number"
        min={1}
        max={remaining}
        value={count}
        onChange={(e) => setCount(e.target.value)}
        style={{ width: 80 }}
        aria-label="Количество тестовых участников"
      />
      <button
        className="btn"
        onClick={handleGenerate}
        disabled={busy || remaining <= 0}
      >
        {busy ? <span className="spinner" /> : <Sparkles size={14} />}
        {busy ? 'Генерируем...' : 'Тестовые'}
      </button>
    </div>
  );
}
